<template lang="pug">
div
  template(v-if="!readonly")
    v-autocomplete.text-sm.round-xl.mt-6.autocmplt(ref="autocmplt" v-model="product" :items="productList" item-text="name" return-object :search-input.sync="searchProducts" background-color="neutral100" placeholder="Search and select here" :loading="isSearching" outlined dense clearable height="48" hide-details="auto" hide-no-data attach  append-icon="" @change="addToList(product)" @focus="fetchProducts()" @blur="clearAutocomplete")
      template(#prepend-inner)
        v-icon.mt-1(size="22") $yzi-search-sm
      template(#item="{ index, item }")
        div.py-3.px-2
          .text-sm.font-normal(v-if="item.uom") {{ item.name }}  ({{item.uom}})
          .text-sm.font-normal(v-else) {{ item.name }}
          .d-flex
            .d-flex.align-center.mr-4
              template(v-if="hasStock(item)")
                v-icon.mr-1(size="11" color="success") $yzi-circle
                .text-xs.font-normal.neutral500--text {{ $t('in_stock') }}
              template(v-else)
                v-icon.mr-1(size="11" color="danger") $yzi-circle
                .text-xs.font-normal.neutral500--text {{ $t('out_of_stock') }}
            .d-flex.align-center.mr-4
              v-icon.mr-1(size="13") {{ getTypeIcon(item) }}
              .text-xs.font-normal.neutral500--text(v-if="item.service") {{ item.service.type | capitalize }}
              .text-xs.font-normal.neutral500--text(v-if="item.medication") {{ item.medication.category | capitalize }}
            template(v-if="item.groups && item.groups.length > 0")
              .d-flex.align-center.mr-4
                v-icon.mr-1(size="13") $yzi-folder
                .text-xs.font-normal.neutral500--text {{ item.groups.join(', ') | capitalize }}
          .text-xxs.font-normal.italic.danger500--text(v-if="item.hasError") {{ item.errorMessage }}

  v-card.mt-5(outlined)
    v-row.pa-4(v-if="products && products.length && !readonly" align="center")
      v-col
        div.text-sm.font-medium.neutral700--text Selected:
      v-col
        div.d-flex.align-center
          v-spacer
          v-btn(@click="clickSearch" icon color="neutral500")
            v-icon(size="20") $yzi-search-sm
          v-text-field(v-if="showSearchField" v-model="searchText" @input="queryChanged" solo dense flat :placeholder="$t('type_to_search')" hide-details clearable autofocus)
          v-menu(offset-y left attach rounded="lg" min-width="100")
            template(#activator="{ on }")
              v-btn.mx-3(icon small v-on="on")
                v-icon $yzi-filter-line
            v-list.py-0
              v-list-item(@click="sortAsc")
                v-list-item-title.text-sm.basic800--text A-Z
              v-divider
              v-list-item(@click="sortDesc")
                v-list-item-title.text-sm.basic800--text Z-A
          y-button(label="Clear all" size="sm" underline button-class="px-0" @click="resetSearch")
      
    v-data-table(:headers="headers" :items="searchText ? filterProducts : products" item-key="id" :items-per-page="pageSize" hide-default-header hide-default-footer disable-sort :loading="isSelecting" :header-props="{ sortIcon: '$yzi-triangle' }")
      template(#no-data)
        div.empty-box.mt-6.pa-8(align="center")
          v-avatar(color="neutral300" size="54")
            v-icon(color="neutral500" size="30") $yzi-colors
          div.text-center.text-md.font-semibold.neutral700--text.mt-6 No inventory selected
          div.text-center.text-sm.font-normal.neutral500--text.mt-1 Selected inventory will be displayed here
      //- name
      template(v-slot:item.name="{ item }")
        div.py-2
          .text-sm.neutral700--text {{ item.name }}
      //- category
      template(v-slot:item.category="{ item }")
        div.py-2
          y-chip(v-if="item.service" label="Service" color="green")
          y-chip(v-if="item.medication" label="Inventory" color="blue")
      //- stock
      template(v-slot:item.stock="{ item }")
        div.d-flex.align-center.py-2(v-if="item.inventory_enabled")
          v-avatar.mr-2(size="8" color="danger")
          div.text-sm.neutral700--text Stock: {{ item.inventory_qty }}
      //- type
      template(v-slot:item.uom="{ item }")
        div.d-flex.align-center.py-2(v-if="item.medication")
          v-icon.mr-2(size="17") $yzi-colors
          div.text-sm.neutral700--text.text-capitalize {{ item.uom }}
      //- action
      template(v-slot:item.action="{ item }")
        div.d-flex.justify-end
          v-btn(icon small @click="deleteProduct(item)")
            v-icon $yzi-trash-03
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    value: {
      required: true,
      type: Array,
      default: (() => [])
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isSelecting: false,
      isSearching: false,
      showSearchField: false,
      searchText: null,
      searchProducts: null,
      product: null,
      pageSize: -1,
      productList: [],
      filterProducts: [],
    }
  },
  computed: { 
    ...mapGetters({ company: 'getCompany' }),
    headers () {
      let headers = [
        { text: 'Name', value: 'name' },
        { text: 'Category', value: 'category', },
        { text: 'Stock', value: 'stock', },
        { text: 'UOM', value: 'uom', },
        { text: '', value: 'action', },
      ]
      if (this.readonly) {
        headers.pop()
      }
      return headers
    },
    products: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    }
  },
  watch: {
    searchProducts (val) {
      if (val) {
        this.fetchProducts(val)
      }
    },
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchProducts (search) {
      this.isSearching = true
      let params = { relations: 'service,medication' }
      if (search) {
        params.search = search
      }
      this.$axios.get(`/marketing/${this.company.slug}/products`, { params })
        .then(resp => {
          this.productList = resp.data.results
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    addToList (product) {
      this.resetSearch()
      if (product) {
        if (this.products.find(f => f.id === product.id)) {
          return
        }
        this.products.push(product)
        this.$refs.autocmplt.blur()
      }
    },
    deleteProduct (product) {
      this.resetSearch()
      this.products = this.products.filter(f => f.id !== product.id)
    },
    clearAutocomplete () {
      this.product = null
      this.searchProducts = null
      setTimeout(() => {
        this.productList = []
      }, 300)
    },
    queryChanged () {
      if (this.searchText) {
        this.filterProducts = this.products.filter(f => f.name.toLowerCase().includes(this.searchText.toLowerCase()))
      }
    },
    clickSearch () {
      this.searchText = null
      this.filterProducts = []
      this.showSearchField = !this.showSearchField
    },
    sortAsc () {
      this.products = this.products.sort((a, b) => a.name.localeCompare(b.name))
    },
    sortDesc () {
      this.products = this.products.sort((a, b) => b.name.localeCompare(a.name))
    },
    hasStock(item) {
      if (!item.inventory_enabled) {
        return true
      }
      if (item.inventory_qty > 0) {
        return true
      }
      return false
    },
    getTypeIcon (item) {
      if (item.medication) {
        return '$yzi-colors'
      }
      if (item.type === 'documents') {
        return '$yzi-file-06'
      }
      return 'mdi-briefcase-outline'
    },
    resetSearch () {
      this.searchText = null
      this.filterProducts = []
      this.showSearchField = false
    }
  }
}
</script>

<style scoped>
.column-width {
  width: 200px;
}
::v-deep .v-data-table__wrapper table tbody tr:hover{
  cursor: pointer;
  background: var(--v-neutral200) !important; 
}
::v-deep table > tbody > tr > td:nth-child(1),
::v-deep table > thead > tr > th:nth-child(1) {
  width: 50%;
}
</style>